import { Component, HostListener, inject, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { EventOriginEnum, NavigateToEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { Equipment } from '@app/core/model/entities/equipments/equipment';
import { ManualWorkflowViewEnum } from '@app/core/model/entities/workflow/workflow';
import { FieldValidator } from '@app/core/model/other/field-validator';
import { BaseEquipmentSheet } from '@app/features/main/views/equipments/equipment-sheet/base-equipment-sheet';
import {
  EquipmentModalService
} from '@app/features/main/views/equipments/equipments-inventory/modals/equipment-modal.service';
import { DocumentModalService } from '@app/features/main/views/organization-documents/modals/document-modal.service';
import { FormStateService } from '@app/shared/components/form-builder/form-state.service';
import { SingleEditService } from '@app/shared/services/single-edit-service';
import { FieldService } from '@services/field.service';
import { of } from 'rxjs';
import { filter, startWith, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'equipments-sidebar',
  templateUrl: './equipments-sidebar.component.html',
  styleUrls: ['./equipments-sidebar.component.scss'],
  providers: [DocumentModalService, EquipmentModalService, FormStateService],
  standalone: false
})
export class EquipmentsSidebarComponent extends BaseEquipmentSheet implements OnInit {

  @Input()
  public formId: string = 'equipment_sidebar';

  public eventsOrigin = EventOriginEnum.SIDEPANEL;

  public validators: FieldValidator[] = [];
  protected readonly EntityType = EntityTypeEnum;
  protected readonly ManualWorkflowViewEnum = ManualWorkflowViewEnum;

  private fieldService = inject(FieldService);
  private singleEditService = inject(SingleEditService);
  private router = inject(Router);


  /**
   * Listen for sidebar toggle actions and updates from the FormStateService.
   * Update sidebar data and call Equipment service when necessary.
   */
  public ngOnInit(): void {
    super.ngOnInit();

    // Update the side panel's content whenever another Equipment is selected
    this.equipmentsService.sidePanelToggle$
      .pipe(
        switchMap(equipmentData => {
          if (equipmentData) {
            return this.fieldService.getField('name', EntityTypeEnum.EQUIPMENT)
              .pipe(
                switchMap(field => {
                  this.validators = field.validators;
                  return this.equipmentsService.equipmentUpdated$;
                }),
                filter(updatedEquipment => updatedEquipment.id === equipmentData.id),
                startWith(equipmentData)
              );
          } else {
            return of(equipmentData);
          }
        }),
        tap(() => {
          this.singleEditService.singleEditSubject.next(null);

          // Unload current Equipment
          if (this.appManager.currentEntity instanceof Equipment) {
            this.appManager.unloadCurrentEntity();
          }
        }),
        filter(equipment => !!equipment),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(equipment => {
        this.equipment = equipment;
        this.images = [];
        this.appManager.currentEntity = equipment;
        this.loadEquipmentData(true);
      });
  }

  /**
   * Open a dialog to delete the current Equipment entity if the user confirms.
   */
  public deleteEquipment(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.DELETE_DIALOG,
      EntityTypeEnum.EQUIPMENT,
      this.equipment.id
    );
    this.equipmentModalService.openDeleteEquipmentsDialog([this.equipment])
      .pipe(
        filter(result => !!result),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.EQUIPMENT_DELETED')));
  }

  /**
   * Open the duplicate Equipment dialog then copy current Equipment.
   */
  public duplicateEquipments(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.DUPLICATE_DIALOG,
      EntityTypeEnum.EQUIPMENT,
      this.equipment.id
    );
    this.equipmentModalService.openDuplicateEquipmentsDialog([this.equipment])
      .pipe(
        filter(result => !!result),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((equipments) => {
        this.router.navigate([
          'organization',
          this.appManager.currentOrganization.id,
          'equipments',
          'equipment-sheet',
          equipments[0].id
        ])
          .catch(console.error);
        this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.EQUIPMENT_DUPLICATED'));
      });
  }

  /**
   * Close the sidebar.
   */
  public closeEquipmentSidebar(): void {
    this.equipmentsService.closeEquipmentSidePanel();
  }

  /**
   * Bypass Material escape handler to call closeEquipmentSidebar,
   * which manage the current entity stacks.
   * @param event
   */
  @HostListener('keyup', ['$event'])
  public handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeEquipmentSidebar();
    }
  }

  /**
   * Navigate to the Equipment's sheet.
   */
  public async navigateToEquipmentSheet(): Promise<void> {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.SHEET,
      EntityTypeEnum.EQUIPMENT,
      this.equipment.id
    );
    await this.equipmentsService.navigateToEquipmentSheet(this.equipment.id);
  }

  /**
   * Open a dialog to upload Documents and link updated Documents to the Equipment entity.
   */
  public uploadEquipmentDocuments(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.UPLOAD_DIALOG,
      EntityTypeEnum.EQUIPMENT,
      this.equipment.id
    );
    super.uploadEquipmentDocuments();
  }

  /**
   * Open a dialog for the user to upload pictures of the Equipment.
   */
  public uploadEquipmentPictures(): void {
    this.analyticsService.trackNavigationEvent(
      EventOriginEnum.SIDEPANEL,
      NavigateToEnum.UPLOAD_DIALOG,
      EntityTypeEnum.EQUIPMENT,
      this.equipment.id
    );
    super.uploadEquipmentPictures();
  }
}
